import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Typography,
  Grid,
  Container,
  useMediaQuery,
} from "@mui/material";
import "./Profile.css";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { getUser, setUser } from "../../store/reducers/user.reducer";
import {
  getUserDetails,
  setProfilePic,
  updateUserDetails,
} from "../../services/APIServices/user.api";
import { useNavigate } from "react-router-dom";
import { ROUTES_PATH } from "../../utils/constants";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useAuth } from "../../context/AuthContext";
import ConfirmBtn from "../../components/ConfirmBtn/ConfirmBtn";
import { logoutUser } from "../UserLogin/AuthSignIn";
import { setChannel } from "../../store/reducers/channel.reducer";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

interface FormData {
  address: string;
  dob: Date | null | any;
  channelList: never[];
  email: string;
  firstName: string;
  lastName: string;
  phoneNo: string;
  profilePic: File | string | null;
  username: string;
  birthDate?: string;
}

const Profile = () => {
  const [formData, setFormData] = useState<FormData>({
    address: "",
    dob: null,
    channelList: [],
    email: "",
    firstName: "",
    lastName: "",
    phoneNo: "",
    profilePic: null,
    username: "",
  });
  const { logout } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const isMobile = useMediaQuery("(max-width:600px)");
  let user = useAppSelector((state) => getUser(state));
  let dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  useEffect(() => {
    if (user && user.username) {
      let { channelList, birthDate, ...userData } = user;

      setFormData((prev) => ({
        ...prev,
        dob: birthDate ? dayjs(birthDate, "DD-MM-YYYY").format("DD/MM/YYYY") : null,
        ...userData
      }));
    } else
      navigate(ROUTES_PATH.LOGIN, {
        state: {
          redirectTo: ROUTES_PATH?.USER_PROFILE,
        },
      });
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleDateChange = (date: any) => {
    setFormData((prevState) => ({
      ...prevState,
      dob: date !== null ? date : null,
    }));
  };

  const handleProfilePicChange = (e: React.ChangeEvent<HTMLInputElement>) => {

    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file!");
        return;
      }

      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > 2) {
        toast.error("Profile picture size should not exceed 2MB!");
        return;
      }

      setFormData((prevState) => ({
        ...prevState,
        profilePic: file,
      }));
      setProfilePic(user?.userID, file).then((res) => {
        if (res?.data?.profilePic) {
          dispatch(setUser({ ...user, profilePic: res?.data?.profilePic }));
        }
      });
    }
  };

  const handleDeleteProfilePic = () => {
    setFormData((prevState) => ({
      ...prevState,
      profilePic: null,
    }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    console.log(formData);
    let { address, dob, email, firstName, lastName, phoneNo, username } =
      formData;
    if (dob) {
      dob = typeof dob === "string" ? dob : dob.format("DD/MM/YYYY");
    }
    const payload = {
      address,
      dob,
      email,
      firstName,
      lastName,
      phoneNo,
      username,
    };
    updateUserDetails(user?.userID, payload).then((res: any) => {
      if (res?.statusCode === 200) {
        dispatch(setUser({ ...user, ...payload }));
        setIsEditing(false);
      }
    });
  };

  const resetProfile = () => {
    if (user) {
      let { channelList, ...userData } = user;
      setFormData((prev) => ({ ...prev, ...userData }));
    }
    setIsEditing(false);
  };

  const handleLogout = async () => {
    try {
      const userID = localStorage.getItem("userID"); // Dynamically fetch
      const videoID = "3965261569"; // Set or get dynamically
      const response = await logoutUser(userID, videoID);

      console.log("Logout successful:", response);

      // Clear token and user info
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("persist:root")
      dispatch(setChannel(null));
      dispatch(setUser(null)); // Update global state
      navigate("/login"); navigate(0);// Redirect to login page
    } catch (error) {
      console.error("Logout failed:", error);
      alert("Logout failed. Please try again.");
    }
  };

  return (
    <Container>
      <Typography variant="h5" gutterBottom>
        {t("userProfile.heading")}
      </Typography>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <div
              style={{
                textAlign: "center",
                marginBottom: "20px",
                position: "relative",
              }}
            >
              <label htmlFor="profile-pic">
                <div
                  style={{
                    width: "150px",
                    height: "150px",
                    borderRadius: "50%",
                    overflow: "hidden",
                    display: "inline-block",
                    border: "2px solid #ccc",
                  }}
                >
                  {formData.profilePic ? (
                    <img
                      src={
                        typeof formData.profilePic === "string"
                          ? formData.profilePic
                          : URL.createObjectURL(formData.profilePic)
                      }
                      alt="Profile"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        fontSize: "20px",
                        color: "#ccc",
                      }}
                    >
                      <CloudUploadIcon fontSize="large" />
                    </div>
                  )}
                </div>
              </label>
              <input
                accept="image/*"
                id="profile-pic"
                type="file"
                onChange={handleProfilePicChange}
                style={{ display: "none" }}
              />
            </div>
          </Grid>
          {/* {formData.profilePic && (
            <Grid item xs={12}>
              <Button
                onClick={handleDeleteProfilePic}
                variant="outlined"
                color="secondary"
                style={{ width: "100%" }}
              >
                Delete Picture
              </Button>
            </Grid>
          )} */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("userProfile.firstName")}
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              inputProps={{
                readOnly: !isEditing,
              }}
              disabled={!isEditing}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("userProfile.lastName")}
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              inputProps={{
                readOnly: !isEditing,
              }}
              disabled={!isEditing}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("userProfile.email")}
              name="email"
              value={formData.email}
              onChange={handleChange}
              inputProps={{
                readOnly: true,
              }}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("userProfile.username")}
              name="username"
              value={formData.username}
              onChange={handleChange}
              inputProps={{
                readOnly: true,
              }}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>

            <LocalizationProvider dateAdapter={AdapterDayjs}>

              {formData.dob !== undefined && (
                <DatePicker
                  value={formData.dob ? dayjs(formData.dob, "DD/MM/YYYY") : null}
                  onChange={(date) => handleDateChange(date)}
                  format="DD/MM/YYYY"
                  label={t("userProfile.dob")}
                  sx={{ width: "100%" }}
                  disabled={!isEditing}
                />
              )}
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("userProfile.phone")}
              name="phoneNo"
              value={formData.phoneNo}
              onChange={handleChange}
              inputProps={{
                readOnly: true,
              }}
              disabled
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t("userProfile.address")}
              name="address"
              value={formData.address}
              onChange={handleChange}
              inputProps={{
                readOnly: !isEditing,
              }}
              disabled={!isEditing}
            />
          </Grid>
          {isEditing ? (
            <>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  type="reset"
                  variant="outlined"
                  color="primary"
                  onClick={() => resetProfile()}
                >
                  {t("cancel")}
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  color="primary"
                >
                  {t("save")}
                </Button>
              </Grid>
            </>
          ) : (
            <Grid item xs={12}>
              <Button
                fullWidth
                type="reset"
                variant="outlined"
                color="secondary"
                onClick={() => setIsEditing(true)}
              >
                {t("userProfile.editProfile")}
              </Button>
            </Grid>
          )}
        </Grid>
      </form>
      {isMobile && (
        <Grid container spacing={2} style={{ marginTop: "20px", justifyContent: "center" }}>
          <Grid item>
            <ConfirmBtn
              onConfirm={handleLogout}
              confirmationMessage={t("logoutDialog.logoutConfirmation")}
              confirmButtonText={t("logoutDialog.logoutConfirm")}
              cancelButtonText={t("logoutDialog.cancel")}
              style={{
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Typography variant="button">
                {t("logoutDialog.logout")}
              </Typography>
            </ConfirmBtn>
          </Grid>
        </Grid>
      )}

    </Container>
  );
};

export default Profile;
